<template>
  <Teleport to="body">
    <Transition name="post-login-banner">
      <div
        v-if="isVisible && activeBanner"
        class="fixed inset-0 z-[4200] flex items-end justify-stretch p-0 sm:p-4 md:items-center md:justify-center md:p-8"
      >
        <div
          class="absolute inset-0 bg-[rgb(var(--color-gray-dark-rgb)/0.38)] backdrop-blur-[3px] md:bg-[rgb(var(--color-gray-dark-rgb)/0.72)] md:backdrop-blur-[8px]"
          @click="closeOverlay"
        />

        <div
          class="post-login-banner__panel relative z-10 flex max-h-[88dvh] w-full flex-col overflow-hidden rounded-t-[28px] border border-white/12 bg-white shadow-[0_24px_64px_rgb(0_0_0_/_0.22)] md:max-h-[calc(100dvh-64px)] md:max-w-[1180px] md:rounded-[28px] md:shadow-[0_36px_120px_rgb(0_0_0_/_0.34)]"
        >
          <div class="flex justify-center pb-1 pt-2 md:hidden">
            <span class="h-1 w-12 rounded-full bg-gray-border"></span>
          </div>

          <div
            class="flex items-start gap-3 border-b border-gray-border px-4 py-3 md:items-center md:gap-4 md:px-7 md:py-4"
          >
            <div class="min-w-0 flex-1">
              <p class="truncate text-[20px] leading-[1.15] font-helvetica-700 font-bold text-gray-dark md:text-[30px]">
                {{ activeBanner.title || "Новость" }}
              </p>
              <p
                v-if="activeBanner.date"
                class="mt-1 text-[12px] font-helvetica text-gray-light md:text-[14px]"
              >
                {{ activeBanner.date }}
              </p>
            </div>

            <div
              v-if="banners.length > 1"
              class="hidden items-center gap-2 rounded-full bg-surface-muted px-3 py-2 md:flex"
            >
              <button
                type="button"
                class="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-gray-border bg-white text-gray-dark transition hover:border-orange hover:text-orange disabled:cursor-not-allowed disabled:opacity-35"
                :disabled="currentIndex <= 0"
                aria-label="Предыдущий баннер"
                @click="prevBanner"
              >
                <svg viewBox="0 0 24 24" class="h-4 w-4" fill="none">
                  <path
                    d="M15 18L9 12l6-6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <span class="min-w-[52px] text-center text-[14px] font-helvetica text-gray-dark">
                {{ currentIndex + 1 }} / {{ banners.length }}
              </span>
              <button
                type="button"
                class="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-gray-border bg-white text-gray-dark transition hover:border-orange hover:text-orange disabled:cursor-not-allowed disabled:opacity-35"
                :disabled="currentIndex >= banners.length - 1"
                aria-label="Следующий баннер"
                @click="nextBanner"
              >
                <svg viewBox="0 0 24 24" class="h-4 w-4" fill="none">
                  <path
                    d="m9 6 6 6-6 6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>

            <button
              type="button"
              class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-gray-border bg-white text-gray-dark transition hover:border-orange hover:text-orange md:h-11 md:w-11"
              aria-label="Закрыть баннеры"
              @click="closeOverlay"
            >
              <svg viewBox="0 0 24 24" class="h-5 w-5" fill="none">
                <path
                  d="M6 6l12 12M18 6 6 18"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>

          <div class="bg-white px-3 py-3 md:bg-surface-muted md:px-6 md:py-6">
            <button
              type="button"
              class="group flex w-full cursor-pointer items-center justify-center overflow-hidden rounded-[22px] bg-surface-muted md:rounded-[24px] md:bg-white"
              :class="activeBanner.link ? 'cursor-pointer' : 'cursor-default'"
              :disabled="!activeBanner.link"
              @click="openBannerLink"
            >
              <img
                :src="activeBanner.image"
                :alt="activeBanner.title || 'Баннер'"
                class="block max-h-[46dvh] w-full object-contain object-center md:max-h-[76vh]"
              />
            </button>
          </div>

          <div
            class="flex flex-col gap-3 border-t border-gray-border px-4 py-3 md:flex-row md:items-center md:justify-between md:gap-4 md:px-7 md:py-4"
          >
            <div class="min-w-0 flex-1">
              <p
                v-if="activeBanner.body"
                class="line-clamp-2 text-[13px] leading-[19px] font-helvetica text-gray-subtle md:line-clamp-3 md:text-[14px] md:leading-[22px]"
              >
                {{ activeBanner.body }}
              </p>
              <p
                v-else
                class="text-[13px] leading-[19px] font-helvetica text-gray-subtle md:text-[14px] md:leading-[22px]"
              >
                {{ activeBanner.link ? "Нажмите на баннер, чтобы открыть ссылку." : "Закройте баннер, чтобы продолжить работу." }}
              </p>
            </div>

            <div class="flex items-center justify-between gap-3 md:justify-start">
              <div
                v-if="banners.length > 1"
                class="flex items-center gap-2 md:hidden"
              >
                <button
                  type="button"
                  class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-gray-border bg-white text-gray-dark transition hover:border-orange hover:text-orange disabled:cursor-not-allowed disabled:opacity-35"
                  :disabled="currentIndex <= 0"
                  aria-label="Предыдущий баннер"
                  @click="prevBanner"
                >
                  <svg viewBox="0 0 24 24" class="h-4 w-4" fill="none">
                    <path
                      d="M15 18L9 12l6-6"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-gray-border bg-white text-gray-dark transition hover:border-orange hover:text-orange disabled:cursor-not-allowed disabled:opacity-35"
                  :disabled="currentIndex >= banners.length - 1"
                  aria-label="Следующий баннер"
                  @click="nextBanner"
                >
                  <svg viewBox="0 0 24 24" class="h-4 w-4" fill="none">
                    <path
                      d="m9 6 6 6-6 6"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>

              <button
                v-if="activeBanner.link"
                type="button"
                class="h-11 min-w-[124px] cursor-pointer rounded-[14px] bg-orange px-5 text-[15px] font-helvetica-700 font-bold text-white transition hover:bg-selected-orange"
                @click="openBannerLink"
              >
                Открыть
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { navigateTo } from "#imports";
import { useHomeNews } from "~/composables/useHomeNews";

const POST_LOGIN_BANNERS_PENDING_KEY = "apsh_post_login_news_pending";

const { postLoginBanners, loadNews, loading } = useHomeNews();

const isVisible = ref(false);
const currentIndex = ref(0);
const shouldTryOpen = ref(false);
const previousBodyOverflow = ref("");

const banners = computed(() => postLoginBanners.value);
const activeBanner = computed(() => banners.value[currentIndex.value] || null);

const consumePendingFlag = () => {
  if (!process.client) return;
  sessionStorage.removeItem(POST_LOGIN_BANNERS_PENDING_KEY);
  shouldTryOpen.value = false;
};

const openOverlay = () => {
  if (!banners.value.length) {
    consumePendingFlag();
    return;
  }

  currentIndex.value = 0;
  isVisible.value = true;
  consumePendingFlag();
};

const closeOverlay = () => {
  isVisible.value = false;
};

const prevBanner = () => {
  currentIndex.value = Math.max(0, currentIndex.value - 1);
};

const nextBanner = () => {
  currentIndex.value = Math.min(banners.value.length - 1, currentIndex.value + 1);
};

const openBannerLink = async () => {
  const link = activeBanner.value?.link?.trim();
  if (!process.client || !link) return;

  if (link.startsWith("/")) {
    closeOverlay();
    await navigateTo(link);
    return;
  }

  try {
    const url = new URL(link, window.location.origin);
    if (url.origin === window.location.origin) {
      closeOverlay();
      await navigateTo(`${url.pathname}${url.search}${url.hash}`);
      return;
    }

    closeOverlay();
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  } catch {
    closeOverlay();
    window.open(link, "_blank", "noopener,noreferrer");
  }
};

watch(isVisible, (value) => {
  if (!process.client) return;

  if (value) {
    previousBodyOverflow.value = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return;
  }

  document.body.style.overflow = previousBodyOverflow.value;
});

watch(
  [shouldTryOpen, banners, loading],
  ([pending, items, isLoading]) => {
    if (!pending || isVisible.value) return;

    if (items.length) {
      openOverlay();
      return;
    }

    if (!isLoading) {
      consumePendingFlag();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  if (!process.client) return;

  shouldTryOpen.value =
    sessionStorage.getItem(POST_LOGIN_BANNERS_PENDING_KEY) === "1";

  if (shouldTryOpen.value) {
    await loadNews();
  }
});

onBeforeUnmount(() => {
  if (!process.client) return;
  document.body.style.overflow = previousBodyOverflow.value;
});
</script>

<style scoped>
.post-login-banner-enter-active,
.post-login-banner-leave-active {
  transition:
    opacity 0.24s ease,
    transform 0.24s ease;
}

.post-login-banner-enter-from,
.post-login-banner-leave-to {
  opacity: 0;
}

.post-login-banner-enter-from .post-login-banner__panel,
.post-login-banner-leave-to .post-login-banner__panel {
  transform: translateY(12px) scale(0.985);
}
</style>
